import type { Notification } from "@/generatedApi";
import { api } from "@/loanLinkApi";
import type { ApiError } from "@/models/ApiError";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { getAuth } from "firebase/auth";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { showNotificationToast } from "@/helpers/notificationToasts";
import { useUserStore } from "./useUserStore";

/**
 * Store for consuming and acting on notifications for the current user.
 * Backend determins current user by looking at auth token.
 */
export const useNotificationStore = defineStore("notifications", () => {
	const all = ref<Notification[]>([]);
	const error = ref<ApiError | null>(null);

	const unread = computed(() => (all.value ?? []).filter((noti) => !noti.read));
	const hasUnread = computed(() => 0 < unread.value.length);
	const isConnected = ref(false);

	let ctrl: AbortController;

	function $reset() {
		all.value = [];
		error.value = null;
		isConnected.value = false;

		if (ctrl) {
			ctrl.abort();
		}
	}

	async function refresh() {
		error.value = null;
		await api.notification
			.getNotifications()
			.then((res) => {
				all.value = res.data ?? [];
			})
			.catch((res) => {
				error.value = res.error;
			});
	}

	async function read(id: string) {
		const notification = all.value.find((noti) => noti.id === id);
		if (!notification) {
			return;
		}

		await api.notification
			.markRead(notification.id)
			.then(() => {
				notification.read = true;
			})
			.catch(() => {
				notification.read = false;
			});
	}

	async function readUnread() {
		await Promise.all(unread.value.map((noti) => read(noti.id)));
	}

	/**
	 * Establishes a realtime connection to receive notification events.
	 */
	async function subscribe() {
		const auth = getAuth();
		const token = await auth.currentUser?.getIdToken();
		if (!token) {
			return;
		}

		const endpointUrl = `${import.meta.env.VITE_API_BASE_URL}/notification/listen`;

		if (!ctrl) {
			ctrl = new AbortController();
		}

		await fetchEventSource(endpointUrl, {
			headers: {
				Authorization: `Bearer ${token}`,
			},

			async onopen() {
				error.value = null;
				isConnected.value = true;
			},

			onerror(error) {
				isConnected.value = false;
				throw error;
			},

			onclose() {
				isConnected.value = false;
			},

			onmessage(message) {
				if (message.event === "notification") {
					const notification = JSON.parse(message.data) as Notification;

					all.value.unshift(notification);
					showNotificationToast(notification);
					void read(notification.id);
				}
			},
			signal: ctrl.signal,
		}).catch((err) => {
			error.value = err;
			isConnected.value = false;
		});
	}

	async function initialize() {
		await refresh();
		await subscribe();
	}

	watch(
		() => useUserStore().user,
		(user) => {
			if (user) {
				void initialize();
			}
		},
		{ immediate: true },
	);

	return {
		all,
		error,
		unread,
		hasUnread,
		isConnected,
		refresh,
		read,
		readUnread,

		$reset,
	};
});
